import React from 'react';
import { Table, Button, Popconfirm, Spin, Alert, Tag, Space } from 'antd';
import { DeleteOutlined, EditOutlined, ReloadOutlined, PauseCircleOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { useDeleteDeal, useToggleDealActive } from '../../../hooks/useDeals';
import { toast } from 'react-toastify';
import moment from 'moment';

const OffersTable = ({
  datas,
  setCurrentDeal,
  setIsModalOpen,
  isLoading,
  error,
  onRefresh
}) => {
  const deleteDealMutation = useDeleteDeal();
  const toggleActiveMutation = useToggleDealActive();

  const handleDelete = async (record) => {
    try {
      await deleteDealMutation.mutateAsync(record._id);
      toast.success('Offre supprimée avec succès');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleToggleActive = async (record) => {
    const isActive = record.active !== false;
    try {
      await toggleActiveMutation.mutateAsync({ dealId: record._id, active: !isActive });
      toast.success(isActive ? 'Offre désactivée' : 'Offre activée');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour de l\'offre');
    }
  };

  const columns = [
    {
      title: 'Offres',
      dataIndex: 'offer',
      key: 'offer',
      render: (text) => <span className="font-medium">{text}</span>,
    },
    {
      title: 'Tags',
      key: 'tagsDescription',
      dataIndex: 'tagsDescription',
      render: (tags) => (
        <>
          {tags && tags.length > 0 ? (
            tags.map((tag, index) => {
              let color = tag.length > 5 ? 'geekblue' : 'green';
              if (tag === 'loser') {
                color = 'volcano';
              }
              return (
                <Tag color={color} key={index}>
                  {tag.toUpperCase()}
                </Tag>
              );
            })
          ) : (
            <span className="text-gray-400">Aucun tag</span>
          )}
        </>
      ),
    },
    {
      title: 'Publiée le',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => (
        <span>{moment(date).format('DD/MM/YYYY')}</span>
      ),
      responsive: ['md'],
    },
    {
      title: 'Expire le',
      dataIndex: 'enddate',
      key: 'enddate',
      render: (date) => (
        <span>
          {date && date !== '2099-12-31' && date !== ''
            ? moment(date).format('DD/MM/YYYY')
            : 'Aucune date'
          }
        </span>
      ),
      responsive: ['md'],
    },
    {
      title: 'Statut',
      dataIndex: 'active',
      key: 'active',
      render: (active) =>
        active !== false ? (
          <Tag color="green">Active</Tag>
        ) : (
          <Tag color="default">Désactivée</Tag>
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const isActive = record.active !== false;
        const isTogglePending = toggleActiveMutation.isPending && toggleActiveMutation.variables?.dealId === record._id;
        return (
          <Space size="small">
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => {
                setCurrentDeal(record); // Passer le record pour la modification
              }}
              size="small"
            >
              Modifier
            </Button>
            <Button
              icon={isActive ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
              onClick={() => handleToggleActive(record)}
              size="small"
              loading={isTogglePending}
              disabled={toggleActiveMutation.isPending}
            >
              {isActive ? 'Désactiver' : 'Activer'}
            </Button>
            <Popconfirm
              title="Êtes-vous sûr de vouloir supprimer cette offre ?"
              onConfirm={() => handleDelete(record)}
              okText="Oui"
              cancelText="Non"
            >
              <Button
                type="primary"
                danger
                icon={<DeleteOutlined />}
                size="small"
                loading={deleteDealMutation.isPending}
              >
                Supprimer
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  // Loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Spin size="large" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert
        message="Erreur lors du chargement des offres"
        description={error.message || 'Une erreur est survenue lors du chargement des données.'}
        type="error"
        showIcon
        action={
          <Button 
            size="small" 
            danger 
            icon={<ReloadOutlined />}
            onClick={onRefresh}
          >
            Réessayer
          </Button>
        }
      />
    );
  }

  return (
    <Table
      columns={columns}
      dataSource={datas}
      rowKey="_id"
      pagination={{
        pageSize: 10,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) => 
          `${range[0]}-${range[1]} sur ${total} offres`,
      }}
    />
  );
};

export default OffersTable;
